import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useInstructorPermissions } from '../hooks/useInstructorPermissions';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const InstructorSkillSheets = () => {
  const { templateId } = useParams();
  const { currentUser, userRole } = useAuth();
  const { permissions, loading: permissionsLoading } = useInstructorPermissions(currentUser?.uid);
  const navigate = useNavigate();

  // State for listing all skill sheets
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // State for viewing individual template
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all skill sheet templates
        const templatesSnap = await getDocs(collection(db, 'skillSheetTemplates'));
        const templatesData = templatesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTemplates(templatesData);

        // Fetch categories
        const categoriesRef = doc(db, 'skillSheetCategories', 'main');
        const categoriesSnap = await getDoc(categoriesRef);
        if (categoriesSnap.exists()) {
          setCategories(categoriesSnap.data().categories || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  // Load specific template if templateId is provided
  useEffect(() => {
    if (!templateId) {
      setCurrentTemplate(null);
      return;
    }

    const fetchTemplate = async () => {
      setTemplateLoading(true);
      try {
        const docRef = doc(db, 'skillSheetTemplates', templateId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCurrentTemplate({ id: templateId, ...docSnap.data() });
        } else {
          alert('Skill sheet not found');
          navigate('/instructor/skill-sheets');
        }
      } catch (error) {
        console.error('Error fetching template:', error);
        alert('Error loading skill sheet');
      }
      setTemplateLoading(false);
    };

    fetchTemplate();
  }, [templateId, navigate]);

  // PDF generation function
  const generatePDF = async () => {
    if (!currentTemplate) return;
    
    const element = document.getElementById('skill-sheet-preview');
    if (!element) return;
    
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${currentTemplate.name || 'skill-sheet'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Duplicate template handler
  const handleDuplicateTemplate = async (template) => {
    try {
      const newName = `${template.name} (Copy)`;
      
      // Create a copy without the id field
      const { id, ...templateWithoutId } = template;
      
      const duplicatedTemplate = {
        ...templateWithoutId,
        name: newName,
        createdBy: currentUser?.uid || 'Unknown',
        creationDate: new Date().toLocaleDateString(),
        lastModified: serverTimestamp(),
      };
      
      // Save the duplicated template to Firebase
      const docRef = await addDoc(collection(db, 'skillSheetTemplates'), duplicatedTemplate);
      
      // Navigate to edit the new template
      navigate(`/skill-sheet-builder/${docRef.id}`);
      
      // Refresh the templates list
      const templatesSnap = await getDocs(collection(db, 'skillSheetTemplates'));
      const templatesData = templatesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error duplicating skill sheet:', error);
      alert('Error duplicating skill sheet: ' + error.message);
    }
  };

  // Filter templates based on search, category, and access level
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(search.toLowerCase()) ||
                         (template.description && template.description.toLowerCase().includes(search.toLowerCase()));
    
    // Category filter
    let matchesCategory = true;
    if (selectedCategory) {
      const category = categories.find(cat => cat.id === selectedCategory);
      matchesCategory = category && category.sheets && category.sheets.includes(template.id);
    }

    // Access level filter
    let hasAccess = true;
    if (userRole === 'Instructor') {
      if (accessLevel === 'general') {
        // General access: can see all templates but can only edit their own
        hasAccess = true;
      } else if (accessLevel === 'special') {
        // Special access: can see and edit all templates
        hasAccess = true;
      }
    }
    
    return matchesSearch && matchesCategory && hasAccess;
  });

  // Check permissions
  if (!currentUser || (userRole !== 'Instructor' && userRole !== 'Admin' && userRole !== 'owner')) {
    return (
      <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h1>
          <p className="text-lg">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Check if instructor has skill sheets permission
  if (userRole === 'Instructor' && (!permissions || permissions.skillSheets === 'none')) {
    return (
      <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">No Access</h1>
          <p className="text-lg">You don't have access to skill sheets. Please contact your administrator.</p>
          <button
            onClick={() => navigate('/instructor-dashboard')}
            className="mt-4 bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get access level for current instructor
  const accessLevel = userRole === 'Instructor' ? permissions?.skillSheets : 'special';
  const hasGeneralAccess = accessLevel === 'general' || accessLevel === 'special';
  const hasSpecialAccess = accessLevel === 'special';
  const canEdit = hasSpecialAccess || userRole === 'Admin' || userRole === 'owner';
  const canCreate = hasGeneralAccess || userRole === 'Admin' || userRole === 'owner';

  // Show individual skill sheet view
  if (templateId && currentTemplate) {
    return (
      <div className="min-h-screen bg-bg text-text py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header with navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/instructor/skill-sheets')}
              className="bg-gray-500 text-white px-4 py-2 rounded shadow hover:bg-gray-700 font-semibold transition duration-200"
            >
              ← Back to Skill Sheets
            </button>
            <h1 className="text-2xl font-bold text-primary">Skill Sheet Viewer</h1>
            <button
              onClick={generatePDF}
              className="bg-green-500 text-white px-4 py-2 rounded shadow hover:bg-green-700 font-semibold transition duration-200"
            >
              📄 Download PDF
            </button>
          </div>

          {/* Template Preview */}
          {templateLoading ? (
            <div className="text-center py-8">
              <div className="text-lg">Loading skill sheet...</div>
            </div>
          ) : (
            <div id="skill-sheet-preview">
              <InstructorSkillSheetPreview template={currentTemplate} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show loading state
  if (templateLoading || permissionsLoading) {
    return (
      <div className="min-h-screen bg-bg text-text flex items-center justify-center">
        <div className="text-lg">Loading skill sheet...</div>
      </div>
    );
  }

  // Show skill sheets list
  return (
    <div className="min-h-screen bg-bg text-text py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/instructor-dashboard')}
            className="bg-gray-500 text-white px-4 py-2 rounded shadow hover:bg-gray-700 font-semibold transition duration-200"
          >
            ← Back to Dashboard
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary">Skill Sheets</h1>
            {userRole === 'Instructor' && (
              <div className="mt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  accessLevel === 'special' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {accessLevel === 'special' ? 'Special Assignment (Full Access)' : 'General Access'}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {canCreate && (
              <button
                onClick={() => navigate('/instructor/skill-sheets/create')}
                className="bg-green-500 text-white px-4 py-2 rounded shadow hover:bg-green-700 font-semibold transition duration-200"
              >
                + Create New
              </button>
            )}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-xl shadow-lg mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              placeholder="Search skill sheets..."
              className="flex-1 border rounded px-3 py-2"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="border rounded px-3 py-2 min-w-[200px]"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Skill Sheets Grid */}
        {loading ? (
          <div className="text-center py-8">
            <div className="text-lg">Loading skill sheets...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-lg text-gray-600">
                  {search || selectedCategory ? 'No skill sheets found matching your filters.' : 'No skill sheets available.'}
                </p>
              </div>
            ) : (
              filteredTemplates.map(template => {
                const isOwner = template.createdBy === currentUser.uid;
                const canEditTemplate = canEdit || isOwner;
                
                return (
                  <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg text-primary">{template.name}</h3>
                      <div className="flex flex-col gap-1">
                        {isOwner && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Your Sheet</span>
                        )}
                        {!isOwner && userRole === 'Instructor' && accessLevel === 'general' && (
                          <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">View Only</span>
                        )}
                      </div>
                    </div>
                    {template.scenarioCategory && (
                      <p className="text-sm text-gray-600 mb-2">{template.scenarioCategory}</p>
                    )}
                    {template.description && (
                      <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                    )}
                    <div className="text-sm text-gray-500 mb-4">
                      {template.rows ? `${template.rows.length} objective${template.rows.length !== 1 ? 's' : ''}` : 'No objectives'}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/instructor/skill-sheets/${template.id}`)}
                        className="flex-1 bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
                      >
                        View Skill Sheet
                      </button>
                      <button
                        onClick={() => handleDuplicateTemplate(template)}
                        className="bg-orange-500 text-white px-4 py-2 rounded shadow hover:bg-orange-700 font-semibold transition duration-200"
                      >
                        Duplicate
                      </button>
                      {canEditTemplate && (
                        <button
                          onClick={() => navigate(`/instructor/skill-sheets/edit/${template.id}`)}
                          className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-700 font-semibold transition duration-200"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Preview component for skill sheets (read-only version)
const InstructorSkillSheetPreview = ({ template }) => (
  <div className="bg-white p-6 rounded-xl border border-primary shadow-lg">
    <div className="mb-6">
      <h2 className="text-2xl font-bold mb-2 text-primary">{template.name}</h2>
      {template.scenarioCategory && <h3 className="text-lg font-semibold mb-2 text-secondary">{template.scenarioCategory}</h3>}
      {template.description && <p className="text-gray-600 mb-4">{template.description}</p>}
    </div>
    
    <table className="w-full editor-table preview-table">
      <thead>
        <tr className="bg-gradient-to-r from-primary/80 to-secondary/80 text-white">
          <th className="text-left p-3">#</th>
          <th className="text-left p-3">Objective</th>
          <th className="text-left p-3">Instructor Notes</th>
          <th className="text-center p-3">Critical?</th>
        </tr>
      </thead>
      <tbody>
        {template.rows && template.rows.length > 0 ? (
          template.rows.map((row, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="p-3 text-center font-semibold">{idx + 1}</td>
              <td className="p-3">{row.objective || 'No objective specified'}</td>
              <td className="p-3">{row.notes || 'No notes'}</td>
              <td className="p-3 text-center">
                {row.critical ? (
                  <span className="text-red-600 font-bold text-lg">✓</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="4" className="p-6 text-center text-gray-500 italic">
              No objectives defined for this skill sheet.
            </td>
          </tr>
        )}
      </tbody>
    </table>

    {/* Summary footer */}
    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
      <div className="flex justify-between text-sm">
        <span>
          <strong>Total Objectives:</strong> {template.rows ? template.rows.length : 0}
        </span>
        <span>
          <strong>Critical Steps:</strong> {template.rows ? template.rows.filter(row => row.critical).length : 0}
        </span>
      </div>
    </div>
  </div>
);

export default InstructorSkillSheets;
